import apiClient from '../../api/common/apiClient.ts';
import { productApi } from '../../api/products/productApi.ts';
import type { ProductDetail } from '../../types/products/product.types.ts';
import { Endpoints } from '@qcdoc/mock-runtime/contracts/endpoints.ts';
import type { ApiResponse, PagedRequest, PagedResult } from '../../types/common/common.ts';
import type {
  CoaListItem,
  CoaExportItem,
  CoaDetail,
  CreateCoaDto,
  UpdateCoaDto,
  CoaAttachment,
  CreateCoaAttachmentDto,
  CoaPoNumber,
  CoaSpecItem,
  CoaVqcaSpec,
  CoaAnalysisResult,
  SubmitCoaDto,
  AddCoaResultsDto,
  CoaSubmitResponse,
  ExperimentResult,
  PerfSpecResult,
} from '../../types/coa/coa.types.ts';

/** api/VQCA/details/{oid} returns every row for the VQCA when oid is the empty GUID. */
const ALL_VQCA_DETAILS = '00000000-0000-0000-0000-000000000000';
const EMPTY_GUID = ALL_VQCA_DETAILS;

export async function getCoaProduct(id: string, signal?: AbortSignal): Promise<ProductDetail | undefined> {
  signal?.throwIfAborted();
  const response = await productApi.getById(id);
  signal?.throwIfAborted();
  return Array.isArray(response.data) ? response.data[0] : response.data;
}

interface RawPo {
  oid: string;
  coaId: string;
  poNumber: string;
  createdBy?: string;
  createdDate?: string;
}

interface RawSpec {
  oid?: string | number;
  specTypeName?: string;
  stpName?: string;
  stpNumber?: string;
  stpurl?: string;
  stpURL?: string;
  min?: number | null;
  max?: number | null;
  numVal?: number | null;
  visual?: boolean | null;
  compare?: string;
  isPerfSpec?: boolean;
}

const mapSpec = (row: RawSpec): CoaSpecItem => ({
  oid: String(row.oid ?? crypto.randomUUID()),
  specTypeName: row.specTypeName ?? '',
  stpName: row.stpName ?? '',
  stpNumber: row.stpNumber ?? '',
  stpUrl: row.stpurl ?? row.stpURL ?? '',
  min: row.min ?? null,
  max: row.max ?? null,
  numVal: row.numVal ?? null,
  visual: row.visual ?? null,
  compare: row.compare ?? '',
  isPerfSpec: row.isPerfSpec ?? false,
});

/** COAResultResponse as returned by api/COAResult/GetByCOAId. */
interface RawAnalysisResult {
  oid: string;
  prodSpecId?: string;
  numVal?: number | null;
  visualVal?: boolean | null;
  resultNotes?: string | null;
  createdBy?: string;
  createdDate?: string | null;
  expId?: string;
  min?: number | null;
  max?: number | null;
  visual?: boolean | null;
  visualQ?: string | null;
  notes?: string | null;
  specTypeName?: string;
  stpName?: string;
  stpNumber?: string;
  stpurl?: string | null;
  stpURL?: string | null;
  isPerfSpec?: boolean | null;
  status?: number;
}

const mapAnalysisResult = (row: RawAnalysisResult): CoaAnalysisResult => ({
  oid: row.oid,
  prodSpecId: row.prodSpecId ?? '',
  numVal: row.numVal ?? null,
  visualVal: row.visualVal ?? null,
  resultNotes: row.resultNotes ?? null,
  createdBy: row.createdBy ?? '',
  createdDate: row.createdDate ?? null,
  expId: row.expId ?? '',
  min: row.min ?? null,
  max: row.max ?? null,
  visual: row.visual ?? null,
  visualQ: row.visualQ ?? null,
  notes: row.notes ?? null,
  specTypeName: row.specTypeName ?? '',
  stpName: row.stpName ?? '',
  stpNumber: row.stpNumber ?? '',
  stpUrl: row.stpurl ?? row.stpURL ?? null,
  isPerfSpec: row.isPerfSpec ?? false,
  status: row.status ?? 0,
});

/**
 * Translate SPA filter keys (camelCase UI keys) into the backend GetCOADTO
 * property names. Unknown keys are passed through untouched so callers can
 * still send raw backend keys (e.g. Statuses, DeleteCOA) directly.
 *
 * Exported so the vendor portal's "My COAs" grid maps its filters identically; the two
 * grids hit the same query builder on the server and must not disagree about key names.
 */
export function mapCoaFilters(filters?: Record<string, unknown>): Record<string, unknown> {
  if (!filters) return {};
  const out: Record<string, unknown> = {};
  const asCsv = (v: unknown) => (Array.isArray(v) ? v.filter(Boolean).join(',') : v);
  const toIso = (v: unknown): string | undefined => {
    if (v == null) return undefined;
    if (typeof v === 'object' && typeof (v as { toISOString?: unknown }).toISOString === 'function') {
      return (v as { toISOString: () => string }).toISOString();
    }
    return String(v);
  };
  /**
   * Date-range fields arrive either as the raw [Dayjs, Dayjs] tuple from the filter panel or,
   * once sanitizeFilterValues has flattened them for the query key, as a 'from,to' string.
   * Only handling the array form silently dropped every date filter.
   */
  const asRange = (v: unknown): [unknown, unknown] | undefined => {
    if (Array.isArray(v)) return [v[0], v[1]];
    if (typeof v === 'string' && v.includes(',')) {
      const [from, to] = v.split(',');
      return [from.trim(), to.trim()];
    }
    return undefined;
  };
  // The backend compares with <=, so a date-only upper bound would exclude everything
  // recorded later that same day.
  const toIsoEndOfDay = (v: unknown): string | undefined => {
    const iso = toIso(v);
    if (!iso) return undefined;
    return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T23:59:59.999` : iso;
  };
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === '') continue;
    switch (key) {
      case 'statusId':
      case 'status':
      case 'statuses':
      case 'Statuses':
        out.Statuses = asCsv(value);
        break;
      case 'deleteCoa':
      case 'DeleteCOA':
        out.DeleteCOA = typeof value === 'boolean' ? (value ? 1 : 0) : Number(value);
        break;
      case 'pslId':
      case 'PslId':
        out.PslId = asCsv(value);
        break;
      case 'lotNumber':
        out.LotNumber = String(value).replace(/\*/g, '%');
        break;
      case 'vendor':
      case 'vendorName':
        out.Company = value;
        break;
      case 'productName': {
        // Backend uses EF LIKE on the exact string — wrap for partial matching.
        const pn = String(value).trim();
        out.ProdName = pn.includes('%') ? pn : `%${pn}%`;
        break;
      }
      case 'poNumber':
        out.PONumber = value;
        break;
      case 'productTypeId':
        out.ProdType = value;
        break;
      case 'issueDate':
      case 'issueDateRange': {
        const range = asRange(value);
        if (range) {
          out.DtFrom = toIso(range[0]);
          out.DtTo = toIsoEndOfDay(range[1]);
        }
        break;
      }
      case 'productionDate':
      case 'productionDateRange': {
        const range = asRange(value);
        if (range) {
          out.DtProdFrom = toIso(range[0]);
          out.DtProdTo = toIsoEndOfDay(range[1]);
        }
        break;
      }
      default:
        out[key] = value;
    }
  }
  return out;
}

/** Raw COAResponse shape returned by the backend (camelCase). */
interface RawCoa {
  pslName?: string;
  productTypeName?: string;
  poNumbers?: string[];
  oid: string;
  productId?: string;
  vendorId?: string;
  vqcaId?: string;
  lotNumber?: string;
  issueDate?: string;
  productionDate?: string;
  status?: number;
  notes?: string;
  createdBy?: string;
  createdDate?: string;
  lastUpdatedBy?: string;
  lastUpdateDate?: string;
  pkgLotNumber?: string;
  prodName?: string;
  shelfLifeDays?: number;
  shelfLifeYears?: number;
  statusName?: string;
  vendorProductName?: string;
  vqcaNotes?: string;
  company?: string;
  firstName?: string;
  mi?: string;
  lastName?: string;
  phone?: string;
  fax?: string;
  mobile?: string;
  address?: string;
  city?: string;
  zip?: string;
  remainingDays?: number;
}

/** COAExportResponse as returned by api/COA/all/export. */
interface RawCoaExport {
  prodName?: string;
  company?: string;
  vendorProductName?: string;
  lotNumber?: string;
  pkgLotNumber?: string;
  statusName?: string;
  issueDate?: string;
  productionDate?: string;
  remainingDays?: number;
}

const mapCoa = (row: RawCoa): CoaListItem => ({
  pslName: row.pslName,
  productTypeName: row.productTypeName,
  poNumbers: row.poNumbers,
  oid: row.oid,
  productName: row.prodName ?? '',
  productOid: row.productId ?? '',
  vendorName: row.company ?? '',
  vendorOid: row.vendorId ?? '',
  vqcaOid: row.vqcaId ?? '',
  vendorProductName: row.vendorProductName ?? '',
  lotNumber: row.lotNumber ?? '',
  pkgLotNumber: row.pkgLotNumber ?? '',
  statusId: row.status ?? 0,
  statusName: row.statusName ?? '',
  issueDate: row.issueDate ?? '',
  productionDate: row.productionDate ?? '',
  createdDate: row.createdDate ?? '',
  createdBy: row.createdBy ?? '',
  notes: row.notes ?? '',
  shelfLifeDays: row.shelfLifeDays ?? 0,
  shelfLifeYears: row.shelfLifeYears ?? 0,
  remainingDays: row.remainingDays ?? 0,
});

export const coaService = {
  /**
   * POST GetCOADTO filters — normalize the bare backend array to the SPA paged
   * shape and resolve the total via the count endpoint for server-side paging.
   */
  getList: async (params: PagedRequest, signal?: AbortSignal, requireCount = false): Promise<{ data: PagedResult<CoaListItem> }> => {
    const page = params.page ?? 1;
    const pageSize = params.pageSize ?? 20;
    const filterBody = mapCoaFilters(params.filters);
    // The count is only used to drive the pager. Awaiting it with Promise.all meant a failing
    // count request rejected the whole call and blanked the grid, so it is settled separately
    // and falls back to the page size when it fails.
    const [listRes, countRes] = await Promise.allSettled([
      apiClient.post<RawCoa[]>(Endpoints.coa.list, {
        ...filterBody,
        StartIndex: (page - 1) * pageSize,
        MaximumRows: pageSize,
        SORTBY: params.sortField,
      }, { ...({}), signal }),
      apiClient.post<number>(Endpoints.coa.count, filterBody, { ...({}), signal }),
    ]);
    if (listRes.status === 'rejected') throw listRes.reason;
    if (requireCount && (countRes.status === 'rejected' || typeof countRes.value.data !== 'number')) {
      throw new Error('Unable to load the complete COA count. Refresh to try again.');
    }
    const items = (listRes.value.data ?? []).map(mapCoa);
    const totalCount =
      countRes.status === 'fulfilled' && typeof countRes.value.data === 'number'
        ? countRes.value.data
        : items.length;
    return {
      data: {
        items, totalCount, page, pageSize,
        totalPages: Math.ceil(totalCount / pageSize),
        hasNextPage: page * pageSize < totalCount,
        hasPreviousPage: page > 1,
      },
    };
  },

  /**
   * Every COA matching the filters, for "Export to Excel".
   *
   * The grids page server-side, so exporting the rows already in the cache would only ever write
   * the page on screen. This hits the dedicated un-paged endpoint instead; paging and the total
   * count are irrelevant here, only the filters and the sort carry over.
   */
  getAllForExport: async (params: PagedRequest, signal?: AbortSignal): Promise<CoaExportItem[]> => {
    const res = await apiClient.post<RawCoaExport[]>(Endpoints.coa.listExport, {
      ...mapCoaFilters(params.filters),
      SORTBY: params.sortField,
    }, { ...({}), signal });
    return (res.data ?? []).map((row) => ({
      productName: row.prodName ?? '',
      vendorName: row.company ?? '',
      vendorProductName: row.vendorProductName ?? '',
      lotNumber: row.lotNumber ?? '',
      pkgLotNumber: row.pkgLotNumber ?? '',
      statusName: row.statusName ?? '',
      issueDate: row.issueDate ?? '',
      productionDate: row.productionDate ?? '',
      remainingDays: row.remainingDays ?? 0,
    }));
  },

  /** GET api/COA/{coaId} — backend returns an array; map the single row to CoaDetail. */
  getById: async (id: string, signal?: AbortSignal): Promise<{ data: CoaDetail | undefined }> => {
    const res = await apiClient.get<RawCoa[]>(Endpoints.coa.byId(id), { ...({}), signal });
    const row = Array.isArray(res.data) ? res.data[0] : (res.data as RawCoa | undefined);
    if (!row) return { data: undefined };
    const base = mapCoa(row);
    return {
      data: {
        ...base,
        lastUpdatedBy: row.lastUpdatedBy ?? '',
        lastUpdateDate: row.lastUpdateDate ?? '',
        vqcaNotes: row.vqcaNotes ?? '',
        contactName: [row.firstName, row.mi, row.lastName].filter(Boolean).join(' '),
        phone: row.phone ?? '',
        fax: row.fax ?? '',
        address: [row.address, row.city, row.zip].filter(Boolean).join(', '),
        results: [],
        attachments: [],
        pos: [],
      },
    };
  },

  /** POST body: AddCOADTO */
  create: (dto: CreateCoaDto, signal?: AbortSignal) =>
    apiClient.post<CoaDetail>(Endpoints.coa.create, {
      ProductId: dto.productId,
      VendorId: dto.vendorId,
      VQCAId: dto.vqcaId,
      LotNumber: dto.lotNumber,
      IssueDate: dto.issueDate,
      ProductionDate: dto.productionDate,
      Status: dto.status,
      Notes: dto.notes ?? '',
      CreatedBy: dto.createdBy,
      CreatedDate: new Date().toISOString(),
      LastUpdatedBy: dto.createdBy,
      LastUpdateDate: new Date().toISOString(),
    }, { ...({}), signal }),

  /** GET ?lotNum=&vqcaId= — returns matching (duplicate) COAs */
  checkDuplicate: (lotNum: string, vqcaId: string, signal?: AbortSignal) =>
    apiClient.get<unknown[]>(Endpoints.coa.duplicateExists, { ...({ params: { lotNum, vqcaId } }), signal }),

  // ── Analysis (legacy ctlCOASpec / ctlPerfSpec) ──

  /** GET api/VQCA/details/{empty}?vqcaId= — the specification rows a COA is measured against. */
  getVqcaSpecs: (vqcaId: string, signal?: AbortSignal) =>
    apiClient.get<CoaVqcaSpec[]>(Endpoints.vqca.byId(ALL_VQCA_DETAILS), { ...({ params: { vqcaId } }), signal }),

  /** POST api/COA/AddVendorCOA — COA + PO numbers + experiment + analysis results in one call. */
  submit: (dto: SubmitCoaDto, signal?: AbortSignal) =>
    apiClient.post<CoaSubmitResponse>(Endpoints.coa.submit, dto, { ...({}), signal }),

  /** POST api/COA/{coaId}/results — record another analysis run against an existing COA. */
  addResults: (coaId: string, dto: AddCoaResultsDto, signal?: AbortSignal) =>
    apiClient.post<CoaSubmitResponse>(Endpoints.coa.addResults(coaId), dto, { ...({}), signal }),

  getVendorWorkflow: (coaId: string, signal?: AbortSignal) =>
    apiClient.get<{
      canAddProductResults: boolean; canAddPerformanceResults: boolean;
      productAttempts: number; performanceAttempts: number;
    }>(`/api/COA/vendor/${coaId}/workflow`, { ...({}), signal }),

  addVendorResults: (coaId: string, dto: AddCoaResultsDto, signal?: AbortSignal) =>
    apiClient.post<CoaSubmitResponse>(`/api/COA/vendor/${coaId}/results`, dto, { ...({}), signal }),

  appendVendorNote: (coaId: string, notes: string, signal?: AbortSignal) =>
    apiClient.post<boolean>('/api/COA/UpdateCOANotes', { coaId, notes, curUser: '' }, { ...({}), signal }),

  /** GET ?COAId={Guid} — every recorded analysis value for a COA. */
  getResults: (coaId: string, signal?: AbortSignal) =>
    apiClient.get<RawAnalysisResult[]>(Endpoints.coaResults.getByCOAId, { ...({
      params: { COAId: coaId, Oid: EMPTY_GUID, expId: EMPTY_GUID },
    }), signal }).then((r) => ({ data: (r.data ?? []).map(mapAnalysisResult) })),

  // ── Certificate sections ──

  /** GET ?coaId={Guid} — PO numbers for a COA */
  getPos: (coaId: string, signal?: AbortSignal) =>
    apiClient.get<RawPo[]>(Endpoints.coa.poByCoa, { ...({ params: { coaId } }), signal }).then((r) => ({
      data: (r.data ?? []).map((p): CoaPoNumber => ({
        oid: p.oid,
        coaId: p.coaId,
        poNumber: p.poNumber,
        createdBy: p.createdBy,
        createdDate: p.createdDate,
      })),
    })),

  /** POST api/COA/AddPONumber */
  addPo: (coaId: string, vendorId: string, poNumber: string, curUser: string, signal?: AbortSignal) =>
    apiClient.post<ApiResponse>(Endpoints.coa.addPo, {
      VendorId: vendorId,
      CoaId: coaId,
      PoNumber: poNumber,
      CurUser: curUser,
    }, { ...({}), signal }),

  /** DELETE api/COA/DeletePONumber/{poId} */
  deletePo: (poId: string, signal?: AbortSignal) =>
    apiClient.delete<ApiResponse>(Endpoints.coa.deletePo(poId), { ...({}), signal }),

  /** GET ?coaId={Guid} — product specifications expected for the COA. */
  getNewSpecs: (coaId: string, signal?: AbortSignal) =>
    apiClient.get<RawSpec[]>(Endpoints.coa.getNewSpecsById, { ...({ params: { coaId } }), signal }).then((r) => ({
      data: (r.data ?? []).map(mapSpec),
    })),

  /** GET ?coaId={Guid} — performance specifications for the COA. */
  getPerfSpecs: (coaId: string, signal?: AbortSignal) =>
    apiClient.get<RawSpec[]>(Endpoints.coa.getPerfSpecByCOA, { ...({ params: { coaId } }), signal }).then((r) => ({
      data: (r.data ?? []).map(mapSpec),
    })),

  /** PUT api/COA/{coaId} body: UpdateCOADTO { LotNum, ProdDate, Notes, CurrentUser } */
  update: (id: string, dto: UpdateCoaDto, signal?: AbortSignal) =>
    apiClient.put<ApiResponse>(Endpoints.coa.update(id), {
      LotNum: dto.lotNumber,
      ProdDate: dto.productionDate,
      Notes: dto.notes,
      CurrentUser: dto.currentUser,
    }, { ...({}), signal }),

  /** DELETE ?coaId={string} (comma-separated GUIDs) — soft delete */
  delete: (ids: string, signal?: AbortSignal) =>
    apiClient.delete<ApiResponse>(Endpoints.coa.delete, { ...({ params: { coaId: ids } }), signal }),

  /** GET ?coaId={string} (comma-separated GUIDs) — restore soft-deleted COAs */
  unDelete: (ids: string, signal?: AbortSignal) =>
    apiClient.get<ApiResponse>(Endpoints.coa.unDelete, { ...({ params: { coaId: ids } }), signal }),

  /**
   * POST ?coaId=&status=&user= — pass / conditionally-approve a COA.
   * status: COAStatus.Passed (3) or COAStatus.ConditionallyApproved (5).
   */
  approve: (id: string, status: number, user: string, signal?: AbortSignal) =>
    apiClient.post<ApiResponse>(Endpoints.coa.approve, null, { ...({
      params: { coaId: id, status, user },
    }), signal }),

  /** POST api/COA/setFailedStatus/{coaId} — fail a COA */
  fail: (id: string, signal?: AbortSignal) =>
    apiClient.post<ApiResponse>(Endpoints.coa.reject(id), undefined, { ...({}), signal }),

  // Attachments — COAAttachmentController
  /** GET ?coaId={Guid} — list attachments (docData may be omitted for lists) */
  getAttachments: (id: string, signal?: AbortSignal) =>
    apiClient.get<CoaAttachment[]>(Endpoints.coaAttachments.byCOA, { ...({ params: { coaId: id } }), signal }),

  /** GET api/COAAttachment/{oid} — single attachment with base64 docData */
  getAttachment: (attachmentId: string, signal?: AbortSignal) =>
    apiClient.get<CoaAttachment>(Endpoints.coaAttachments.byId(attachmentId), { ...({}), signal }),

  /** POST api/COAAttachment/coa — upload a COA attachment (base64 docData) */
  addAttachment: (dto: CreateCoaAttachmentDto, signal?: AbortSignal) =>
    apiClient.post<ApiResponse>(Endpoints.coaAttachments.create, {
      DocName: dto.docName,
      DocType: dto.docType,
      DocData: dto.docData,
      CoaId: dto.coaId,
      CreatedBy: dto.createdBy,
      CreatedDate: new Date().toISOString(),
      SiteName: dto.siteName ?? '',
    }, { ...({}), signal }),

  /** DELETE api/COAAttachment/{oid} */
  deleteAttachment: (_id: string, attachmentId: string, signal?: AbortSignal) =>
    apiClient.delete<ApiResponse>(Endpoints.coaAttachments.delete(attachmentId), { ...({}), signal }),

  // Experiment Results — COAResultController
  /** GET ?expId={Guid}&COAId={Guid} */
  getExperimentResults: (coaId: string, expId?: string, signal?: AbortSignal) =>
    apiClient.get<ExperimentResult[]>(Endpoints.coaResults.get, { ...({
      params: { COAId: coaId, expId: expId ?? '' },
    }), signal }),

  /** GET ?expId={Guid}&COAId={Guid} → performance spec results */
  getPerfSpecResults: (coaId: string, expId?: string, signal?: AbortSignal) =>
    apiClient.get<PerfSpecResult[]>(Endpoints.coaResults.perfDetails, { ...({
      params: { COAId: coaId, expId: expId ?? '' },
    }), signal }),
};
